/* ===========================================================
   Lucro do período — função PURA que reescala/reconcilia a cascata-base do mock
   para um período e devolve os agregados do P&L (Vendas, Custos, Marketing, Lucro)
   + os deltas vs. período anterior. Centraliza o "cálculo de dinheiro" que o
   CLAUDE.md proíbe nascer em componente; consumida por screens/lucro (cards +
   waterfall). Coberta por profit-period.test.ts.

   Modelo de demo (database-schema §6): só o período "este-mês" é exato; os demais
   escalam linearmente pela razão de receita líquida e a linha "Anúncios" absorve a
   variação de margem para o total fechar no lucro real. Consequência conhecida (não
   bug): custos variáveis escalam com a receita, então a variação % de Custos
   acompanha a de Vendas — em produção, com custo real por período, cada um se move
   sozinho.
   =========================================================== */
import type { PeriodoLucro, PeriodoRef, WaterfallStep } from "../api/types";
import { relativeDelta } from "./delta";

export interface AdsSplit {
  google: number;
  meta: number;
  total: number;
}

export interface ProfitDeltas {
  vendas: number | null;
  custos: number | null;
  marketing: number | null;
  lucro: number | null;
}

export interface ProfitPeriod {
  /** Cascata escalada e reconciliada ao lucro real do período (valores assinados). */
  steps: WaterfallStep[];
  /** Receita bruta escalada — teto das barras do waterfall. */
  receitaBaseEscalada: number;
  /** Receita bruta do período (magnitude). */
  vendasBrutas: number;
  /** Custos variáveis do período (magnitudes positivas) + total. */
  custos: {
    cmv: number;
    descontos: number;
    frete: number;
    imposto: number;
    embalagem: number;
    total: number;
  };
  /** Mídia paga do período, com split por plataforma (magnitudes positivas). */
  marketing: { total: number; google: number; meta: number };
  /** Margem de contribuição = Vendas − Custos (antes da mídia). */
  mc: number;
  /** Lucro real do período (mídia já descontada). */
  lucroReal: number;
  /** Lucro a exibir: com mídia = lucroReal; sem mídia = assume zero anúncio (= mc). */
  lucroExibido: number;
  deltas: ProfitDeltas;
}

const abs = Math.abs;

export function profitPeriod(args: {
  waterfall: WaterfallStep[];
  periodo: PeriodoLucro;
  anterior: PeriodoRef | null;
  ads: AdsSplit;
  mediaConn: boolean;
}): ProfitPeriod {
  const { waterfall, periodo, anterior, ads, mediaConn } = args;

  const valorBase = (r: string) => abs(waterfall.find((s) => s.rotulo === r)?.valor ?? 0);
  const receitaBase = waterfall.find((s) => s.tipo === "base")?.valor ?? periodo.receita;
  const descontosBase = waterfall
    .filter((s) => s.rotulo === "Descontos")
    .reduce((acc, s) => acc + abs(s.valor), 0);
  const receitaLiquidaBase = receitaBase - descontosBase;
  const escalaPara = (receitaLiq: number) => receitaLiq / receitaLiquidaBase;
  const fator = escalaPara(periodo.receita);

  // Escala a cascata-base e reconcilia o fim ao lucro REAL do período: o total vira
  // periodo.lucro e "Anúncios" absorve a variação de margem (só "este-mês" é exato).
  const escalados = waterfall.map((s) => ({ ...s, valor: Math.round(s.valor * fator) }));
  const mcEscalada = escalados.find((s) => s.rotulo === "Margem de contribuição")?.valor ?? 0;
  const steps: WaterfallStep[] = escalados.map((s) => {
    if (s.tipo === "total") return { ...s, valor: periodo.lucro };
    if (s.rotulo === "Anúncios") return { ...s, valor: -(mcEscalada - periodo.lucro) };
    return s;
  });

  const valorStep = (r: string) => abs(steps.find((s) => s.rotulo === r)?.valor ?? 0);
  const vendasBrutas = valorStep("Receita bruta");
  const cmv = valorStep("CMV");
  const descontos = valorStep("Descontos");
  const frete = valorStep("Frete");
  const imposto = valorStep("Imposto");
  const embalagem = valorStep("Embalagens");
  const custos = {
    cmv,
    descontos,
    frete,
    imposto,
    embalagem,
    total: cmv + descontos + frete + imposto + embalagem,
  };
  const midia = valorStep("Anúncios");
  const googleShare = ads.total ? ads.google / ads.total : 0;
  const google = Math.round(midia * googleShare);
  const marketing = { total: midia, google, meta: midia - google };
  const mc = steps.find((s) => s.rotulo === "Margem de contribuição")?.valor ?? 0;
  const lucroReal = periodo.lucro;
  // Sem mídia conectada (#2 / decisions.md §18, revisado): assume zero anúncio — o
  // lucro para na margem de contribuição (= Vendas − Custos), sem descontar mídia.
  const lucroExibido = mediaConn ? lucroReal : vendasBrutas - custos.total;

  // Período anterior (para os deltas): a cascata-base reescalada para a receita
  // anterior. PeriodoRef só guarda receita/lucro — daí o modelo de escala.
  const custosBaseAbs = waterfall
    .filter((s) => s.tipo === "sub" && s.rotulo !== "Anúncios")
    .reduce((acc, s) => acc + abs(s.valor), 0);
  const mcBase = valorBase("Margem de contribuição");
  const vendasBrutasAnt = anterior
    ? Math.round(valorBase("Receita bruta") * escalaPara(anterior.receita))
    : null;
  const custosAnt = anterior ? Math.round(custosBaseAbs * escalaPara(anterior.receita)) : null;
  const midiaAnt = anterior
    ? abs(Math.round(mcBase * escalaPara(anterior.receita)) - anterior.lucro)
    : null;
  const lucroAnt = mediaConn
    ? anterior
      ? anterior.lucro
      : null
    : vendasBrutasAnt != null && custosAnt != null
      ? vendasBrutasAnt - custosAnt
      : null;

  const deltas: ProfitDeltas = {
    vendas: vendasBrutasAnt != null ? relativeDelta(vendasBrutas, vendasBrutasAnt) : null,
    custos: custosAnt != null ? relativeDelta(custos.total, custosAnt) : null,
    marketing: midiaAnt != null ? relativeDelta(midia, midiaAnt) : null,
    lucro: lucroAnt != null ? relativeDelta(lucroExibido, lucroAnt) : null,
  };

  return {
    steps,
    receitaBaseEscalada: receitaBase * fator,
    vendasBrutas,
    custos,
    marketing,
    mc,
    lucroReal,
    lucroExibido,
    deltas,
  };
}
